import { loadingFrame, logoLayout, STATIC_LOGO_PATH } from '@canopy/brand'
import { useEffect, useRef } from 'react'

/** Shared brand mark and loading animation; display size determines the optical stroke. */
export function CanopyLogo({
  size = 34,
  animated = false,
  transparent = animated,
}: {
  size?: number
  animated?: boolean
  transparent?: boolean
}) {
  const path = useRef<SVGPathElement>(null)
  const rotor = useRef<SVGGElement>(null)
  const layout = logoLayout(size)
  useEffect(() => {
    let request = 0
    const reset = () => {
      path.current?.setAttribute('d', STATIC_LOGO_PATH)
      rotor.current?.removeAttribute('transform')
    }
    reset()
    if (!animated) return
    const reducedMotion = matchMedia('(prefers-reduced-motion: reduce)')
    const start = () => {
      cancelAnimationFrame(request)
      reset()
      if (!animated || reducedMotion.matches || document.hidden) return
      const started = performance.now()
      let previous = -Infinity
      const tick = (now: number) => {
        if (now - previous >= 1000 / 30) {
          const frame = loadingFrame((now - started) / 1000)
          path.current?.setAttribute('d', frame.path)
          rotor.current?.setAttribute(
            'transform',
            `translate(512 512) rotate(${frame.angle}) scale(${frame.scale}) translate(-512 -512)`,
          )
          previous = now
        }
        request = requestAnimationFrame(tick)
      }
      request = requestAnimationFrame(tick)
    }
    start()
    reducedMotion.addEventListener('change', start)
    document.addEventListener('visibilitychange', start)
    return () => {
      cancelAnimationFrame(request)
      reducedMotion.removeEventListener('change', start)
      document.removeEventListener('visibilitychange', start)
    }
  }, [animated])
  return (
    <svg
      width={size}
      height={size}
      viewBox={layout.viewBox}
      aria-hidden="true"
      data-canopy-logo={animated ? 'loading' : 'static'}
      style={{
        display: 'block',
        flexShrink: 0,
        borderRadius: Math.round(size * 0.22),
      }}
    >
      {!transparent ? (
        <rect
          x={layout.origin}
          y={layout.origin}
          width={layout.span}
          height={layout.span}
          fill="#000"
        />
      ) : null}
      <g
        ref={rotor}
        fill="none"
        stroke={transparent ? 'var(--loading-ink, #000)' : '#fff'}
        strokeWidth={layout.strokeWidth}
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path ref={path} d={STATIC_LOGO_PATH} />
        <circle cx={512} cy={512} r={layout.hubRadius} />
      </g>
    </svg>
  )
}
